// Plan content used on overview cards + dedicated detail pages
import {
  HeartPulse,
  Shield,
  Stethoscope,
  Smile,
  Building2,
  Ribbon,
  HandHeart,
  Flower2,
  Clock,
} from "lucide-react";

export const PLANS = [
  {
    slug: "aca-health",
    name: "ACA Health",
    short: "Individual & family marketplace plans with subsidies for those who qualify.",
    icon: HeartPulse,
    tone: "from-sky-50 to-white",
    audience: "Under 65 · Individuals, families, self-employed",
    external_cta: {
      label: "Search Plans Now",
      url: "https://www.healthsherpa.com/?_agent_id=christian-regalado",
    },
    bullets: [
      "Marketplace plans on Healthcare.gov",
      "Premium subsidies & cost-share help",
      "Preventive care covered at 100%",
      "Open Enrollment + Special Enrollment guidance",
    ],
    details:
      "Affordable Care Act plans cover essential health benefits — doctor visits, prescriptions, maternity, mental health and more. As your local broker, I shop every available carrier in your county and walk you through subsidies so you only pay what you should.",
    faqs: [
      {
        q: "When can I enroll in an ACA plan?",
        a: "Open Enrollment runs Nov 1 – Jan 15 each year. You can also enroll outside that window if you experience a qualifying life event such as a move, marriage, new baby, or loss of coverage.",
      },
      {
        q: "Do I qualify for a subsidy?",
        a: "Most households earning between 100% – 400%+ of the federal poverty level qualify for premium tax credits. We'll run the numbers for you in minutes — no obligation.",
      },
      {
        q: "Can I keep my doctor?",
        a: "We always check your preferred doctors and prescriptions against each plan's network and formulary before you enroll.",
      },
    ],
  },
  {
    slug: "medicare-advantage",
    name: "Medicare Advantage",
    short: "All-in-one Part C plans that bundle hospital, medical, drug coverage — often $0/month.",
    icon: Shield,
    tone: "from-blue-50 to-white",
    audience: "65+ · or qualifying disability",
    bullets: [
      "$0 premium options in most FL counties",
      "Dental, vision, hearing & gym benefits",
      "Prescription drug coverage included",
      "HMO, PPO and Special Needs Plans",
    ],
    details:
      "Medicare Advantage (Part C) plans replace Original Medicare and bundle extra benefits at little or no monthly premium. I compare every carrier serving Walton, Bay, Okaloosa and surrounding counties so you get the network, drugs and perks that actually fit your life.",
    faqs: [
      {
        q: "When can I switch Medicare Advantage plans?",
        a: "The Annual Election Period runs Oct 15 – Dec 7. Newly eligible beneficiaries get a 7-month Initial Enrollment Period around their 65th birthday. There's also a Medicare Advantage Open Enrollment Jan 1 – Mar 31.",
      },
      {
        q: "Will I lose Original Medicare?",
        a: "No. You're still enrolled in Medicare — the plan simply administers your Part A and Part B benefits, usually with extras like dental, vision and a Part D drug plan built in.",
      },
      {
        q: "Are my prescriptions covered?",
        a: "Most Medicare Advantage plans include Part D. I run a full Medicare.gov drug comparison so you see your exact yearly out-of-pocket before you enroll.",
      },
    ],
  },
  {
    slug: "medicare-supplement",
    name: "Medicare Supplement",
    short: "Medigap plans that cover what Original Medicare doesn't — deductibles, copays, coinsurance.",
    icon: Stethoscope,
    tone: "from-amber-50 to-white",
    audience: "65+ on Original Medicare",
    bullets: [
      "Use any doctor that accepts Medicare",
      "Predictable monthly cost, minimal surprises",
      "Plans G, N and High-Deductible G compared",
      "No network restrictions, nationwide travel",
    ],
    details:
      "Medicare Supplement (Medigap) plans pair with Original Medicare to cover the gaps — Part A & B deductibles, the 20% coinsurance, and excess charges. If freedom to see any Medicare-accepting doctor matters most, this is usually the right path. I help you decide between Plan G, Plan N and high-deductible options based on your budget and health.",
    faqs: [
      {
        q: "What's the best time to buy a Medigap plan?",
        a: "Your 6-month Medigap Open Enrollment Period starts the month you turn 65 and are enrolled in Part B. During this window you cannot be denied or charged more for pre-existing conditions.",
      },
      {
        q: "Do I still need a drug plan?",
        a: "Yes. Medigap plans don't include prescription drugs, so we'll pair yours with a standalone Part D plan that fits your medications.",
      },
      {
        q: "Will my premium go up every year?",
        a: "While Florida has a Issue Age pricing benefit, it is vital to choose an insurer with a history of low rate increases. I will be happy to review the pricing history of your preferred carrier with you.",
      },
    ],
  },
  {
    slug: "short-term-medical",
    name: "Short Term Medical",
    short: "Bridge coverage between plans — flexible term lengths, quick underwriting, budget-friendly premiums.",
    icon: Clock,
    tone: "from-cyan-50 to-white",
    audience: "Under 65 · Missed OEP, between jobs, or need a leaner premium",
    bullets: [
      "Coverage from 1 to 12 months",
      "Fast application, quick approval",
      "Pairs well with hospital indemnity",
      "Available year-round (no enrollment window)",
    ],
    details:
      "Short Term Medical and ACA-alternative plans give you a coverage bridge when you can't (or don't want to) buy a marketplace plan right now. Watch the slideshow and pick a carrier — Christian is already on file as your broker on every enrollment page.",
    faqs: [],
  },
  {
    slug: "dental",
    name: "Dental",
    short: "Standalone dental plans with preventive, basic and major coverage — solo or family.",
    icon: Smile,
    tone: "from-teal-50 to-white",
    audience: "All ages",
    external_cta: {
      label: "Search Local Dental Plans Now",
      url: "/dental/search",
    },
    bullets: [
      "100% preventive on most plans",
      "PPO + HMO options, no waiting period plans available",
      "Add vision and hearing bundles",
      "Great fit alongside ACA or Medicare",
    ],
    details:
      "Whether you're filling gaps your medical plan doesn't cover or building a family benefits package, a dedicated dental plan keeps cleanings, fillings, crowns and orthodontia predictable. I compare premiums, networks and annual maximums in plain English.",
    faqs: [
      {
        q: "Are there waiting periods?",
        a: "Some plans waive waiting periods on basic and major services if you've had prior coverage. We'll check your history and find no-wait options when possible.",
      },
      {
        q: "Can I keep my dentist?",
        a: "Most PPO plans let you stay with your current dentist and just pay more out-of-network. We confirm before you enroll.",
      },
      {
        q: "Is orthodontia covered?",
        a: "Several plans include child or adult orthodontia. I'll flag those options for families specifically.",
      },
    ],
  },
  {
    slug: "hospital-indemnity",
    name: "Hospital Indemnity",
    short: "Cash benefits paid directly to you when you're hospitalized — pairs perfectly with high-deductible plans.",
    icon: Building2,
    tone: "from-rose-50 to-white",
    audience: "Anyone with a high-deductible plan",
    external_cta: {
      label: "See Hospital Indemnity Plans",
      url: "https://customer.enroll.natgenhealth.com/quick-quote/?agent=CfDJ8KcuJeU1UfVFhwatU8NLwQl7-pF18Le6lw2-62Tl1CzVtMNfxrBEKvkNzZ2Dh-y_cvEe2-wKp-uCdrKU1D1sEBIuKQ&product=hospital-expense",
    },
    bullets: [
      "Lump-sum or per-day hospital cash",
      "Use the money for anything — deductibles, groceries, mortgage",
      "No network restrictions",
      "Affordable monthly premiums",
    ],
    details:
      "Hospital Indemnity is a supplemental policy that pays you directly when you're admitted. It's a smart shock-absorber for ACA bronze plans, high-deductible Medicare Advantage plans, or anyone who wants extra protection without changing primary coverage.",
    faqs: [
      {
        q: "Do I need a hospital indemnity plan if I already have insurance?",
        a: "It's not required, but it's one of the most affordable ways to cover what your main plan doesn't — deductibles, transportation, lost wages, child care. Many clients add one for $20–$40/month.",
      },
      {
        q: "How fast do claims pay?",
        a: "Most carriers issue benefits within 7–14 days of admission. You receive the money directly.",
      },
      {
        q: "Can I have multiple supplemental plans?",
        a: "Yes. Hospital Indemnity pairs well with critical illness, accident and cancer plans depending on your risk profile.",
      },
    ],
  },
  {
    slug: "cancer-plans",
    name: "Cancer Plans",
    short: "Lump-sum cash on diagnosis to cover treatment, travel, lost income — whatever you need most.",
    icon: Ribbon,
    tone: "from-pink-50 to-white",
    audience: "All ages · Anyone wanting financial protection from cancer costs",
    external_cta: {
      label: "See Cancer-Only Plans",
      url: "https://customer.enroll.natgenhealth.com/quick-quote/?agent=CfDJ8KcuJeU1UfVFhwatU8NLwQl7-pF18Le6lw2-62Tl1CzVtMNfxrBEKvkNzZ2Dh-y_cvEe2-wKp-uCdrKU1D1sEBIuKQ&product=cancer-only",
    },
    bullets: [
      "Lump-sum benefit paid on first diagnosis",
      "Pairs with any health plan",
      "Helps cover travel to specialty centers",
      "Affordable monthly premiums",
    ],
    details:
      "Cancer insurance pays a lump-sum cash benefit (or itemized treatment benefits) the moment you're diagnosed with a covered cancer. You can use it for anything — high deductibles, experimental treatments, second opinions at MD Anderson or Mayo, travel and lodging, or simply to keep the household running while you focus on getting well.",
    faqs: [
      {
        q: "Why add cancer coverage if I already have health insurance?",
        a: "Even great health plans leave gaps — deductibles, coinsurance, out-of-network specialists, transportation, lost income. A lump-sum cancer benefit gives you immediate liquidity to handle whatever comes up.",
      },
      {
        q: "How quickly does it pay out?",
        a: "Most carriers pay the diagnosis benefit within days of receiving confirmed pathology. You decide how to use the money.",
      },
      {
        q: "Are pre-existing conditions covered?",
        a: "Prior cancer diagnoses are typically excluded, but the lookback period and underwriting vary by carrier. We'll find the most favorable option for your history.",
      },
    ],
  },
  {
    slug: "long-term-care",
    name: "Long Term Care",
    short: "Coverage for in-home care, assisted living, and nursing care — protecting your retirement assets.",
    icon: HandHeart,
    tone: "from-emerald-50 to-white",
    audience: "Typically 50–75 · Planning ahead for future care needs",
    bullets: [
      "In-home care, assisted living & nursing facility",
      "Traditional LTC or hybrid life/LTC policies",
      "Protect retirement assets and family",
      "Tax-qualified options available",
    ],
    details:
      "Long Term Care (LTC) coverage pays for the help most of us will eventually need — bathing, dressing, mobility, memory care — that health insurance and Medicare simply don't cover. Modern hybrid policies combine LTC benefits with a life insurance benefit, so if you never need care your family still receives a tax-free payout. The right time to buy is while you're still healthy.",
    faqs: [
      {
        q: "Doesn't Medicare cover nursing homes?",
        a: "Only briefly — Medicare pays for up to 100 days of skilled rehab following a qualifying hospital stay. Long-term custodial care (which is what most people end up needing) is not covered.",
      },
      {
        q: "When should I start looking at LTC?",
        a: "Most clients buy between ages 55 and 65, while they're healthy enough to qualify and premiums are still reasonable. Waiting too long can mean denial or unaffordable rates.",
      },
      {
        q: "What if I never need long-term care?",
        a: "With a hybrid LTC/life policy, the unused balance is paid to your beneficiary as a tax-free death benefit. You're never paying for something you don't use.",
      },
    ],
  },
  {
    slug: "final-expense",
    name: "Final Expense",
    short: "Simplified-issue whole life to cover funeral costs and final bills — no medical exam.",
    icon: Flower2,
    tone: "from-amber-50 to-white",
    audience: "Typically 50–85 · Anyone wanting to protect loved ones",
    bullets: [
      "$5,000 – $50,000 coverage amounts",
      "No medical exam — simplified health questions",
      "Fixed premium that never increases",
      "Beneficiary receives tax-free cash",
    ],
    details:
      "Final Expense (sometimes called burial or funeral insurance) is whole life insurance built to cover end-of-life costs — funeral, casket or cremation, medical bills, small debts — without burdening your family. There's no medical exam, just a few health questions. The premium and benefit are locked in for life. Your beneficiary receives the money tax-free, typically within a few business days.",
    faqs: [
      {
        q: "How much does a funeral actually cost?",
        a: "The national median for a traditional funeral is $8,000–$12,000, and prices on the Emerald Coast tend to run on the higher end. We'll size your policy to match.",
      },
      {
        q: "Is there a medical exam?",
        a: "No exam required. You'll answer a short list of health questions and we'll match you with the most lenient carrier for your situation. Approvals are usually same-day.",
      },
      {
        q: "When does my family receive the benefit?",
        a: "Once a death certificate is filed, most carriers pay claims within a few business days, directly to the beneficiary you name. No probate, no waiting on an estate.",
      },
    ],
  },
];

export const getPlanBySlug = (slug) => PLANS.find((p) => p.slug === slug);
